import { SourceRef, SourceResolverOptions } from '../types/sources';
import { StreamSegmentInput, AudioSegmentInput } from '../types/manifest';

export class SourceResolver {
  private blobUrls: Map<string, string> = new Map();
  private options: SourceResolverOptions;

  constructor(options: Partial<SourceResolverOptions> = {}) {
    this.options = {
      preferLocal: true,
      createBlobUrls: true,
      autoCleanup: true,
      ...options,
    };
  }

  resolve(
    segment: StreamSegmentInput,
    streamType: 'screen' | 'webcam',
    pendingBlobs: Map<string, Blob>,
    segmentBlobs?: Map<string, Map<number, Blob>>,
    pendingBlobUrls?: Map<string, string>
  ): SourceRef {
    const blobKey = `${streamType}-${segment.segmentIndex}`;

    if (this.options.preferLocal) {
      const pendingBlob = pendingBlobs.get(blobKey);
      if (pendingBlob) {
        return this.createBlobSource(blobKey, pendingBlob);
      }

      if (segmentBlobs) {
        const streamBlobMap = segmentBlobs.get(streamType);
        if (streamBlobMap) {
          const blob = streamBlobMap.get(segment.segmentIndex);
          if (blob) {
            return this.createBlobSource(blobKey, blob);
          }
        }
      }
      
      if (pendingBlobUrls) {
        const existingUrl = pendingBlobUrls.get(blobKey);
        if (existingUrl) {
          return {
            type: 'blob',
            value: existingUrl,
            status: 'ready',
          };
        }
      }
    }

    if (segment.key && segment.uploadStatus === 'completed') {
      return {
        type: 'url',
        value: segment.key,
        s3Key: segment.key,
        status: 'ready',
      };
    }

    if (segment.key) {
      return {
        type: 'url',
        value: segment.key,
        s3Key: segment.key,
        status: 'ready',
      };
    }

    // If segment has no key and no blob, it might still be initializing
    // Return loading status instead of error for better UX during initial render
    if (segment.uploadStatus === 'pending' || !segment.uploadStatus) {
      return {
        type: 'pending',
        value: '',
        status: 'loading',
        error: `Waiting for ${streamType} segment ${segment.segmentIndex} blob`,
      };
    }

    return {
      type: 'pending',
      value: '',
      status: 'error',
      error: `No source available for ${streamType} segment ${segment.segmentIndex}`,
    };
  }

  private createBlobSource(key: string, blob: Blob): SourceRef {
    if (this.blobUrls.has(key)) {
      return {
        type: 'blob',
        value: this.blobUrls.get(key)!,
        originalBlob: blob,
        status: 'ready',
      };
    }

    if (this.options.createBlobUrls) {
      const blobUrl = URL.createObjectURL(blob);
      this.blobUrls.set(key, blobUrl);
      return {
        type: 'blob',
        value: blobUrl,
        originalBlob: blob,
        status: 'ready',
      };
    }

    return {
      type: 'pending',
      value: '',
      status: 'error',
      error: 'Blob URL creation disabled',
    };
  }

  /**
   * Resolve audio segment source from local blobs or S3 URLs
   * Similar to video segment resolution but for audio-only segments
   */
  resolveAudio(
    segment: AudioSegmentInput,
    pendingBlobs: Map<string, Blob>,
    segmentBlobs?: Map<string, Map<number, Blob>>,
    pendingBlobUrls?: Map<string, string>
  ): SourceRef {
    const blobKey = `audio-${segment.segmentIndex}`;

    if (this.options.preferLocal) {
      // Check pending blobs first
      const pendingBlob = pendingBlobs.get(blobKey);
      if (pendingBlob) {
        return this.createBlobSource(blobKey, pendingBlob);
      }

      // Check segment blobs map
      if (segmentBlobs) {
        const audioBlobMap = segmentBlobs.get('audio');
        if (audioBlobMap) {
          const blob = audioBlobMap.get(segment.segmentIndex);
          if (blob) {
            return this.createBlobSource(blobKey, blob);
          }
        }
      }
      
      // Check for existing blob URLs
      if (pendingBlobUrls) {
        const existingUrl = pendingBlobUrls.get(blobKey);
        if (existingUrl) {
          return {
            type: 'blob',
            value: existingUrl,
            status: 'ready',
          };
        }
      }
    }

    // Use S3 key if available
    if (segment.key && segment.uploadStatus === 'completed') {
      return {
        type: 'url',
        value: segment.key,
        s3Key: segment.key,
        status: 'ready',
      };
    }

    if (segment.key) {
      return {
        type: 'url',
        value: segment.key,
        s3Key: segment.key,
        status: 'ready',
      };
    }

    // Segment still initializing
    if (segment.uploadStatus === 'pending' || !segment.uploadStatus) {
      return {
        type: 'pending',
        value: '',
        status: 'loading',
        error: `Waiting for audio segment ${segment.segmentIndex} blob`,
      };
    }

    return {
      type: 'pending',
      value: '',
      status: 'error',
      error: `No source available for audio segment ${segment.segmentIndex}`,
    };
  }

  cleanup(): void {
    this.blobUrls.forEach((url, key) => {
      URL.revokeObjectURL(url);
      console.log(`[SourceResolver] Revoked blob URL for ${key}`);
    });
    this.blobUrls.clear();
  }

  getBlobUrlCount(): number {
    return this.blobUrls.size;
  }
}
